/**
 * Heatmap renderer: a `bins x bins` grid painted as filled cells.
 *
 * The backend bins in SQL and returns the grid, so this draws ~2,500 rectangles
 * whatever the range holds - which is the whole point. Where a scatter of 200k
 * points saturates into a solid shape, the same data as a heatmap shows *how
 * much* is where, exactly, with no sampling.
 *
 * uPlot has no heatmap series type, so the cells go on in a `draw` hook - the
 * same trick `render-scatter.ts` uses for its point cloud, and it buys the same
 * things: real axes, drag-zoom, the resize machinery and image export all keep
 * working. One invisible series carries the data arrays uPlot insists on; both
 * scales are pinned to the returned edges, since there are no point values for
 * it to autoscale from.
 *
 * `buildHeatmapOpts` is split out so a grid panel can mount the same drawing
 * with its own size and mount point.
 */

import type uPlot from 'uplot';
import { plotState, type HeatScale, type PlotState } from '../../../state/plot-state.ts';
import { colorScaleGradientCss } from '../plot-constants.ts';
import { colorScaleRgb } from './color-scale.ts';
import { runtime } from '../plot-runtime.ts';
import { colorLegend } from '../plot-ui-state.ts';
import { themeColors, chartContainerSize, type ThemeColors } from './chart-dom.ts';
import { buildXAxis, buildYAxis, rebuildUplot } from './uplot-helpers.ts';
import { formatNum, escHtml } from './format.ts';
import type { HeatmapResponse } from '../../../api/types.ts';
import { placeTooltip } from './tooltip.ts';

/** Quantized ramp: one `fillStyle` per bucket rather than one per cell. */
const RAMP_BUCKETS = 64;

/**
 * Effective ramp shape. `auto` means log for density and linear for an
 * aggregate: counts pile up in a few cells and trail off over thousands, so a
 * linear ramp on density paints one bright square and a uniform dark field.
 */
export function effectiveScale(data: HeatmapResponse, mode: HeatScale): HeatScale {
  if (mode !== 'auto') return mode;
  return data.z_column ? 'linear' : 'log';
}

/**
 * Normalizer from cell value to 0..1 along the ramp.
 *
 * Log and sqrt need a positive floor; a range that cannot support the requested
 * shape (an aggregate spanning zero, say) falls back to linear rather than
 * drawing NaNs.
 */
export function rampPosition(
  lo: number,
  hi: number,
  scale: HeatScale,
): (v: number) => number {
  const span = hi - lo;
  const linear = (v: number): number => (span > 0 ? (v - lo) / span : 1);
  if (scale === 'log' && lo > 0 && hi > lo) {
    const a = Math.log(lo);
    const b = Math.log(hi);
    return (v) => (v > 0 ? (Math.log(v) - a) / (b - a) : 0);
  }
  if (scale === 'sqrt' && lo >= 0 && hi > lo) {
    const a = Math.sqrt(lo);
    const b = Math.sqrt(hi);
    return (v) => (v >= 0 ? (Math.sqrt(v) - a) / (b - a) : 0);
  }
  return linear;
}

/** The value colouring each cell: the aggregate where there is one, else density. */
function cellValues(data: HeatmapResponse): Array<number | null> {
  if (data.values) return data.values;
  // A zero-count cell is empty, not a zero - it must stay unpainted.
  return data.counts.map((n) => (n > 0 ? n : null));
}

/**
 * Integer pixel boundaries for a set of edge values.
 *
 * Rounding once, here, is what makes the grid read as a grid: cells then share
 * exact boundaries instead of each rounding its own width and leaving hairline
 * seams and one-pixel overlaps - the thing that made the first pass look like
 * scattered rectangles rather than a matrix.
 */
function pixelEdges(u: uPlot, edges: number[], scale: 'x' | 'y'): number[] {
  return edges.map((v) => Math.round(u.valToPos(v, scale, true)));
}

/** Axis ticks placed on cell boundaries, thinned to about `target` labels. */
export function edgeSplits(edges: number[], target = 8): number[] {
  const n = edges.length - 1;
  if (n <= 0) return edges;
  const step = Math.max(1, Math.ceil(n / target));
  const idx: number[] = [];
  for (let i = 0; i <= n; i += step) idx.push(i);
  // Always label the final boundary. When the previous tick is nearly on top of
  // it, take its place instead of crowding beside it.
  const lastIdx = idx[idx.length - 1]!;
  if (lastIdx !== n) {
    if (n - lastIdx < step / 2) idx[idx.length - 1] = n;
    else idx.push(n);
  }
  return idx.map((i) => edges[i]!);
}

/** Cell separators are drawn only once a cell is big enough to have an inside. */
const SEPARATOR_MIN_PX = 12;

/** The draw hook that paints the grid. Shared by the single chart and grid panels. */
export function heatmapDrawHook(
  data: HeatmapResponse,
  c: ThemeColors,
  state?: PlotState,
): (u: uPlot) => void {
  const s = state ?? plotState.value;
  const values = cellValues(data);
  const lo = data.z_min ?? 0;
  const hi = data.z_max ?? 1;
  const scale = effectiveScale(data, s.style.heat_scale);
  const pos = rampPosition(lo, hi, scale);
  const scaleName = s.color_scale || 'Viridis';
  const ramp: string[] = [];
  for (let b = 0; b < RAMP_BUCKETS; b++) {
    ramp.push(colorScaleRgb(scaleName, (b + 0.5) / RAMP_BUCKETS));
  }

  return (u: uPlot): void => {
    const ctx = u.ctx;
    const { nx, ny, x_edges: xe, y_edges: ye } = data;
    if (xe.length < 2 || ye.length < 2) return;
    const xp = pixelEdges(u, xe, 'x');
    const yp = pixelEdges(u, ye, 'y');
    const { left, top, width, height } = u.bbox;

    ctx.save();
    // Clip to the plot area: a cell straddling the edge of a zoomed view must
    // not paint over the axes.
    ctx.beginPath();
    ctx.rect(left, top, width, height);
    ctx.clip();

    // The empty grid, painted first. Without it the occupied cells float on the
    // page and the chart reads as a scatter of squares; with it they sit in a
    // matrix, and "nothing here" is visibly part of the same field.
    ctx.fillStyle = c.grid;
    ctx.globalAlpha = 0.22;
    ctx.fillRect(
      Math.max(left, Math.min(xp[0]!, xp[nx]!)),
      Math.max(top, Math.min(yp[0]!, yp[ny]!)),
      Math.min(width, Math.abs(xp[nx]! - xp[0]!)),
      Math.min(height, Math.abs(yp[ny]! - yp[0]!)),
    );
    ctx.globalAlpha = 1;

    for (let iy = 0; iy < ny; iy++) {
      // y grows upward on screen, so the row's top edge is the higher value.
      const yTop = Math.min(yp[iy]!, yp[iy + 1]!);
      const yBot = Math.max(yp[iy]!, yp[iy + 1]!);
      if (yBot < top || yTop > top + height) continue;
      for (let ix = 0; ix < nx; ix++) {
        const v = values[iy * nx + ix];
        if (v == null || !Number.isFinite(v)) continue;
        const xLeft = Math.min(xp[ix]!, xp[ix + 1]!);
        const xRight = Math.max(xp[ix]!, xp[ix + 1]!);
        if (xRight < left || xLeft > left + width) continue;
        let t = pos(v);
        if (!Number.isFinite(t)) t = 0;
        else if (t < 0) t = 0;
        else if (t > 1) t = 1;
        const bucket = Math.min(RAMP_BUCKETS - 1, (t * RAMP_BUCKETS) | 0);
        ctx.fillStyle = ramp[bucket]!;
        // Shared integer boundaries, so neighbours tile exactly: at least 1px
        // wide, since a sub-pixel cell that rounds to nothing would read as a
        // hole in the data.
        ctx.fillRect(xLeft, yTop, Math.max(1, xRight - xLeft), Math.max(1, yBot - yTop));
      }
    }

    // Cell separators, on the same integer boundaries as the fills. Skipped
    // once cells are small - at 100 bins the lines would be most of the chart.
    const cw = Math.abs(xp[nx]! - xp[0]!) / nx;
    const ch = Math.abs(yp[ny]! - yp[0]!) / ny;
    if (cw >= SEPARATOR_MIN_PX && ch >= SEPARATOR_MIN_PX) {
      ctx.strokeStyle = c.grid;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      for (let i = 0; i <= nx; i++) {
        const x = xp[i]! + 0.5;
        ctx.moveTo(x, Math.min(yp[0]!, yp[ny]!));
        ctx.lineTo(x, Math.max(yp[0]!, yp[ny]!));
      }
      for (let i = 0; i <= ny; i++) {
        const y = yp[i]! + 0.5;
        ctx.moveTo(Math.min(xp[0]!, xp[nx]!), y);
        ctx.lineTo(Math.max(xp[0]!, xp[nx]!), y);
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    ctx.restore();
  };
}

/** Cell under the cursor, or null when it is outside the grid. */
function cellAt(u: uPlot, data: HeatmapResponse): { ix: number; iy: number } | null {
  const left = u.cursor.left;
  const top = u.cursor.top;
  if (left == null || top == null || left < 0 || top < 0) return null;
  const xv = u.posToVal(left, 'x');
  const yv = u.posToVal(top, 'y');
  const { x_edges: xe, y_edges: ye, nx, ny } = data;
  const x0 = xe[0]!;
  const y0 = ye[0]!;
  const xw = (xe[nx]! - x0) / nx;
  const yw = (ye[ny]! - y0) / ny;
  if (xw <= 0 || yw <= 0) return null;
  const ix = Math.floor((xv - x0) / xw);
  const iy = Math.floor((yv - y0) / yw);
  if (ix < 0 || iy < 0 || ix >= nx || iy >= ny) return null;
  return { ix, iy };
}

/**
 * Cell readout. Written straight into the tooltip node rather than through
 * `updateTooltip`, which keys everything off a series index - a heatmap has no
 * series, and the thing under the pointer is a cell with two ranges.
 */
export function heatmapTooltipHook(
  data: HeatmapResponse,
  target?: { el: HTMLElement; container: HTMLElement },
): (u: uPlot) => void {
  return (u: uPlot): void => {
    const tt = target?.el ?? runtime.tooltipEl;
    if (!tt) return;
    const cell = cellAt(u, data);
    if (!cell || !u.over) {
      tt.classList.remove('visible');
      tt.dataset.ttIdx = '';
      return;
    }
    const { ix, iy } = cell;
    const at = iy * data.nx + ix;
    const key = `hm:${at}`;
    if (tt.dataset.ttIdx !== key) {
      const n = data.counts[at] ?? 0;
      const v = data.values ? data.values[at] : null;
      const rows = [
        `<div class="uptt-time">${escHtml(data.x_column)} ${formatNum(data.x_edges[ix]!)} – ${formatNum(data.x_edges[ix + 1]!)}</div>`,
        `<div class="uptt-time">${escHtml(data.y_column)} ${formatNum(data.y_edges[iy]!)} – ${formatNum(data.y_edges[iy + 1]!)}</div>`,
      ];
      if (data.z_column) {
        const label = `${data.z_agg ?? 'avg'} ${data.z_column}`;
        rows.push(
          `<div class="uptt-row"><span class="uptt-name">${escHtml(label)}</span>` +
            `<span class="uptt-val">${v == null ? '-' : formatNum(v)}</span></div>`,
        );
      }
      rows.push(
        `<div class="uptt-row"><span class="uptt-name">rows</span>` +
          `<span class="uptt-val">${n.toLocaleString()}</span></div>`,
      );
      tt.innerHTML = rows.join('');
      tt.dataset.ttIdx = key;
    }
    tt.classList.add('visible');

    const container = target?.container ?? runtime.containerEl;
    if (container) placeTooltip(u, tt, container);
  };
}

/**
 * uPlot options for a heatmap. `size` and the tooltip target let a grid panel
 * reuse this; the single chart measures its own mount and uses the shared node.
 */
export function buildHeatmapOpts(
  data: HeatmapResponse,
  c: ThemeColors,
  size: { width: number; height: number },
  target?: { el: HTMLElement; container: HTMLElement },
  state?: PlotState,
): { opts: uPlot.Options; uData: uPlot.AlignedData } {
  const { x_edges: xe, y_edges: ye, nx, ny } = data;
  const x0 = xe[0] ?? 0;
  const x1 = xe[nx] ?? 1;
  const y0 = ye[0] ?? 0;
  const y1 = ye[ny] ?? 1;

  // One invisible series: uPlot needs an x array and at least one y array, and
  // the cells are painted by hand. The x values are the cell centres, so the
  // cursor still has something to snap to at each column.
  const centres: number[] = [];
  for (let i = 0; i < nx; i++) centres.push((xe[i]! + xe[i + 1]!) / 2);
  const uData: uPlot.AlignedData = [centres, centres.map(() => null)];

  // Ticks - and so uPlot's own gridlines - land on cell boundaries rather than
  // on round numbers of its choosing. A gridline through the middle of a cell
  // is what makes a heatmap look like squares scattered over a line chart.
  const fontPx = (state ?? plotState.value).style.font_size;
  const xAxis = buildXAxis(c, false, data.x_column, fontPx);
  xAxis.splits = () => edgeSplits(xe);
  const yAxis = buildYAxis(c, 'y', 'left', data.y_column, fontPx);
  yAxis.splits = () => edgeSplits(ye);

  const opts: uPlot.Options = {
    ...size,
    // Both scales are pinned: the only y values in the data are nulls, so there
    // is nothing for uPlot to range from.
    scales: {
      x: { time: false, range: (): [number, number] => [x0, x1] },
      y: { range: (): [number, number] => [y0, y1] },
    },
    axes: [xAxis, yAxis],
    series: [{}, { label: data.y_column, paths: () => null, points: { show: false } }],
    legend: { show: false },
    cursor: { x: true, y: true, drag: { x: false, y: false } },
    hooks: {
      draw: [heatmapDrawHook(data, c, state)],
      setCursor: [heatmapTooltipHook(data, target)],
    },
  };
  return { opts, uData };
}

/** Colour-ramp legend below the chart, labelled with what the colour means. */
export function heatmapLegend(data: HeatmapResponse): void {
  if (data.z_min == null || data.z_max == null) {
    colorLegend.set(null);
    return;
  }
  const scale = effectiveScale(data, plotState.value.style.heat_scale);
  const what = data.z_column ? `${data.z_agg ?? 'avg'} ${data.z_column}` : 'rows per cell';
  colorLegend.set({
    colorBy: scale === 'linear' ? what : `${what} (${scale})`,
    min: data.z_min,
    max: data.z_max,
    gradientCss: colorScaleGradientCss(plotState.value.color_scale || 'Viridis'),
  });
}

export function renderHeatmap(data: HeatmapResponse): void {
  const c = themeColors();
  const { opts, uData } = buildHeatmapOpts(data, c, chartContainerSize());
  rebuildUplot(uData, opts);
  heatmapLegend(data);
}
